import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  MouseEvent,
  ReactNode,
} from "react";
import { Oval } from "react-loader-spinner";
import { Icon } from "../icons/icon";

export type ButtonVariations =
  | "primary"
  | "primary-dark"
  | "primary-purple"
  | "primary-white"
  | "secondary"
  | "secondary-white"
  | "secondary-2"
  | "secondary-dark"
  | "secondary-light"
  | "secondary-light-2"
  | "secondary-light-3"
  | "plain"
  | "plain-2"
  | "plain-3"
  | "plain-purple"
  | "plain-danger"
  | "danger";

export const Button = ({
  text,
  onClick = () => {},
  type = "submit",
  className = "",
  loading,
  disabled,
  icon,
  iconClassName = "",
  variation = "primary",
  loaderWidth = "25",
  loaderHeight = "25",
  size = "regular",
  extra,
}: {
  text: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  iconClassName?: string;
  variation?: ButtonVariations;
  loaderWidth?: string;
  loaderHeight?: string;
  size?: "regular" | "small" | "medium";
  extra?: ReactNode;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>) => {
  const getColors = () => {
    switch (variation) {
      case "primary":
        return "bg-pry-green-200 border-pry-green-200 text-white";
      case "primary-dark":
        return "bg-pry-dark-green-300 border-pry-dark-green-300 text-white";
      case "primary-purple":
        return "bg-info-purple border-info-purple text-white";
      case "primary-white":
        return "border-white bg-white text-pry-green-200";
      case "secondary":
        return "bg-transparent border-pry-green-200 text-pry-green-200";
      case "secondary-white":
        return "bg-transparent border-white text-white";
      case "secondary-2":
        return "bg-white border-pry-dark-green-300/20 text-pry-dark-green-300";
      case "secondary-dark":
        return "bg-transparent border-pry-dark-green-400/80 text-pry-dark-green-400/80";
      case "secondary-light":
        return "bg-light-green-bg-100 border-light-green-bg-100 text-pry-green-200";
      case "secondary-light-2":
        return "bg-light-green-bg-300 border-light-green-bg-300 text-pry-dark-green-300";
      case "secondary-light-3":
        return "!bg-light-green-bg !border-light-green-bg !text-pry-green-200";
      case "plain":
        return "bg-transparent border-transparent text-pry-green-200";
      case "plain-2":
        return "bg-white border-pry-dark-green-300/20 text-pry-dark-green-300";
      case "plain-3":
        return "bg-white border-light-green-bg-300 text-pry-green-200";
      case "plain-purple":
        return "text-info-purple bg-transparent border-transparent";
      case "plain-danger":
        return "text-red-200 bg-transparent border-transparent";
      case "danger":
        return "bg-red-300 border-red-300 text-white";
      default:
        break;
    }
  };

  return (
    <button
      type={type}
      onClick={(e) => {
        if (type !== "submit") {
          e.preventDefault();
        }
        onClick(e);
      }}
      disabled={disabled || loading}
      className={`
      ${getColors()}
      border
      flex gap-2 items-center justify-center
      w-full rounded-[4px]
      ${size === "regular" && "py-3 md:py-[14px]"}
      ${size === "small" && "py-[8px] px-[10px]"}
      ${size === "medium" && "py-2 md:py-[10px] px-3.5"}
      text-sm font-medium
      lg:hover:scale-95
      relative
      disabled:hover:scale-100 disabled:cursor-not-allowed disabled:opacity-40
      ${loading && "!text-transparent"}
      transition-all
      ${className}
    `}
    >
      {icon && (
        <Icon
          svg={icon}
          width={16}
          height={16}
          className={loading ? "opacity-0" : iconClassName}
        />
      )}
      {text}
      {extra && extra}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Oval
            visible={true}
            height={loaderHeight}
            width={loaderWidth}
            ariaLabel="oval-loading"
            wrapperStyle={{}}
            wrapperClass=""
            color="#ffffff"
            strokeWidth={1}
            strokeWidthSecondary={5}
          />
        </div>
      )}
    </button>
  );
};
